import { useMemo, type FunctionComponent } from "react";
import { ExclamationCircleFilled, SettingOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Badge, Dropdown, List, Menu, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import calendar from "dayjs/plugin/calendar";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import keyBy from "lodash.keyby";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";

import { chatActions } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { UserRole } from "@fitness-app/data-models";
import { ChatStatus, type ChatChannelWithLastMessage, type ChatMember } from "@fitness-app/data-models/entities/Chat";
import { getAttachmentPreview } from "@fitness-app/utils/src/chat/attachmentPreview";
import { CALENDAR_LABELS } from "@fitness-app/utils/src/constants/calendar";

import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { useMarkChannelAsResponded, type ChannelMetricInfo } from "~/modules/Chat/hooks/useChannelResponseMetrics";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

import("dayjs/locale/pl");

dayjs.extend(localizedFormat);
dayjs.extend(calendar);
dayjs.extend(relativeTime);

interface OwnProps {
  channel: ChatChannelWithLastMessage;
  archived: boolean;
  changeTab: (key: ChatStatus) => void;
  responseMetric?: ChannelMetricInfo;
}

type Props = OwnProps;

const renderDate = (timestamp: Date | string) => dayjs(timestamp).locale("pl").calendar(null, CALENDAR_LABELS);

const urgencyToBadgeStatus: Record<string, "success" | "warning" | "error"> = {
  green: "success",
  warning: "warning",
  red: "error",
};

const ChatChannelItem: FunctionComponent<Props> = ({ channel, archived, changeTab, responseMetric }) => {
  const dispatch = useAppDispatch();
  const markAsResponded = useMarkChannelAsResponded();
  const { t } = useTranslation(["chat"]);
  const userId = useAppSelector((store) => store.user.data?.id);
  const { isClient } = useUserRole();
  // Archiwizacja kanału i „oznacz jako odpowiedziane" to prowadzenie czatów
  // całego konta, nie rola ACCOUNT_OWNER.
  const canManageChannels = usePermission(PermissionKey.CHAT_WRITE, PermissionScope.ALL);
  const updatingChannelId = useAppSelector((store) => store.chat.updatingChannelId);
  const usersArray: ChatMember[] = useMemo(() => Object.values(channel.members), [channel.members]);
  const members = channel.members ? usersArray.filter((chatMember) => chatMember.uid !== userId) : null;

  const unreadForChannel = (
    useAppSelector((store) => store.chat.unreadMessages?.perChannel?.[channel.id], shallowEqual) || []
  ).length;
  const clients = usersArray.filter((user) => user.role === UserRole.CLIENT);
  const trainers = usersArray.filter((user) => user.role === UserRole.TRAINER);
  const teamMembers = usersArray.filter(
    (user) => user.role === UserRole.TEAM_MEMBER && !user.disabled && channel.assignedUsers.includes(user.uid),
  );
  const profilesById = channel.profiles ? keyBy(channel.profiles, "id") : null;

  const getResponseTimeTooltip = (deadlineAt: number | null): string => {
    if (!deadlineAt) return "";
    const deadline = dayjs.unix(deadlineAt).locale("pl");
    const now = dayjs();
    const deadlineFormatted = deadline.format("DD.MM.YYYY HH:mm");

    if (deadline.isBefore(now)) {
      return t("responseTime.overdue", { time: now.to(deadline, true), deadline: deadlineFormatted });
    }
    return t("responseTime.remaining", { time: deadline.fromNow(true), deadline: deadlineFormatted });
  };

  const renderResponseStatusBadge = () => {
    if (!responseMetric?.urgency) return null;

    const badgeStatus = urgencyToBadgeStatus[responseMetric.urgency];
    const tooltipText = getResponseTimeTooltip(responseMetric.deadlineAt);
    const isOverdue = responseMetric.urgency === "red";

    return (
      <Tooltip title={tooltipText}>
        <span style={{ marginLeft: 8, cursor: "pointer" }}>
          {isOverdue ? (
            <ExclamationCircleFilled style={{ color: "var(--ant-error-color)", fontSize: 14 }} />
          ) : (
            <Badge status={badgeStatus} />
          )}
        </span>
      </Tooltip>
    );
  };

  const renderDescription = () => {
    if (archived) {
      return `Zarchiwizowano ${dayjs(channel.updatedAt).format("DD-MM-YY HH:mm")}`;
    }
    const badge = renderResponseStatusBadge();
    if (!channel.lastMessage?.createdAt) return "Napisz pierwszą wiadomość!";
    return (
      <div>
        <div className="flex items-center">
          {renderDate(channel.lastMessage.createdAt)}
          {badge}
        </div>
        <div>
          {truncate(channel.lastMessage.content || getAttachmentPreview(channel.lastMessage.files), { length: 30 })}
        </div>
      </div>
    );
  };

  const renderTitle = (chatMember?: ChatMember) => {
    if (clients.length === 1) {
      const [member] = chatMember ? [chatMember] : clients;

      const profile = member ? profilesById?.[member.uid] || member : member;

      if (!profile) {
        return <>Rozmowa</>;
      }

      if (channel.name) {
        // The channel name renders on BOTH branches — the profiles join resolves through
        // chat_member, and when it hits, dropping the name made an announcement channel row
        // indistinguishable from the client's regular conversation (TRE-1947).
        const displayName = "name" in profile ? profile.name : `${profile.firstName} ${profile.lastName}`;
        return <>{`${displayName} - (${channel.name})`}</>;
      }

      return <>{"name" in profile ? profile.name : `${profile.firstName} ${profile.lastName}`}</>;
    }

    if (members?.length === 1) {
      const [member] = members;

      const profile = member ? profilesById?.[member.uid] || member : member;

      if (!profile) {
        return <>Rozmowa</>;
      }

      return <>{"name" in profile ? profile.name : `${profile.firstName} ${profile.lastName}`}</>;
    }
    return <>Rozmowa grupowa</>;
  };

  if (!members?.length) {
    return null;
  }

  if (members.length === 1 || clients.length === 1) {
    const [member] = isClient ? (teamMembers.length ? teamMembers : trainers) : clients.length ? clients : members;

    // `profiles` resolves through `chat_member`, which has no row for most team members, so the
    // join returns nothing for them and this used to render no avatar at all. Fall back to the
    // member snapshot — but only when the profile is missing entirely, so a deliberately cleared
    // avatar does not bring the stale one back (TRE-1729). Same shape as `renderTitle` above.
    const profile = member?.uid ? profilesById?.[member.uid] : undefined;
    const avatarUrl = profile ? profile.avatarUrl : member?.avatarUrl;

    return (
      <Tooltip placement="left" title={archived ? "Aktywuj czat aby móc zobaczyć treść i zacząć pisać" : undefined}>
        <List.Item
          onClick={() => {
            if (archived) return;
            dispatch(chatActions.addChannelToList(channel));
          }}
          className="px-2"
        >
          <List.Item.Meta
            avatar={
              <div>
                <Badge count={unreadForChannel}>
                  <Avatar shape="circle" icon={<UserOutlined />} src={avatarUrl ? avatarUrl : undefined} size={38} />
                </Badge>
              </div>
            }
            title={renderTitle(member)}
            description={<div style={{ fontSize: 13 }}>{renderDescription()}</div>}
          />
          {(member?.role !== UserRole.SYSTEM && canManageChannels) || (responseMetric && !archived) ? (
            <Dropdown
              // @ts-expect-error - click event is not supported in Dropdown
              onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
              overlay={
                <Menu>
                  {responseMetric && !archived && (
                    <Menu.Item>
                      <a
                        role="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await markAsResponded.mutateAsync(channel.id);
                        }}
                      >
                        {t("responseTime.markAsResponded")}
                      </a>
                    </Menu.Item>
                  )}
                  {canManageChannels && (
                    <Menu.Item>
                      <a
                        role="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          await dispatch(
                            chatActions.updateChannelStatus({
                              channelId: channel.id,
                              status: archived ? ChatStatus.Active : ChatStatus.Archived,
                            }),
                          );
                          if (archived) {
                            changeTab(ChatStatus.Active);
                          }
                        }}
                        // className={classes.menuItem}
                      >
                        {archived ? "Aktywuj" : "Zarchiwizuj"}
                      </a>
                    </Menu.Item>
                  )}
                </Menu>
              }
              trigger={["hover"]}
            >
              {updatingChannelId === channel.id || markAsResponded.isLoading ? (
                <Spin size="small" />
              ) : (
                <SettingOutlined />
              )}
            </Dropdown>
          ) : null}
        </List.Item>
      </Tooltip>
    );
  }

  return (
    <Tooltip placement="left" title={archived ? "Aktywuj czat aby móc zobaczyć treść i zacząć pisać" : undefined}>
      <List.Item
        onClick={() => {
          if (archived) return;
          dispatch(chatActions.addChannelToList(channel));
        }}
        // className={classNames(classes.dropdownMenu, archived && classes.archived)}
      >
        <List.Item.Meta
          avatar={
            <div>
              <Badge count={unreadForChannel}>
                <Avatar.Group>
                  {members.map((member) => (
                    <Avatar shape="circle" icon={<UserOutlined />} src={member?.avatarUrl} size={38} key={member.uid} />
                  ))}
                </Avatar.Group>
              </Badge>
            </div>
          }
          title={renderTitle()}
          description={<div style={{ fontSize: 13 }}>{renderDescription()}</div>}
        />
        {canManageChannels && (
          <Dropdown
            overlay={
              <Menu onClick={({ domEvent }) => domEvent.stopPropagation()}>
                {responseMetric && !archived && (
                  <Menu.Item>
                    <a
                      role="button"
                      onClick={async (e) => {
                        e.stopPropagation();
                        await markAsResponded.mutateAsync(channel.id);
                      }}
                    >
                      {t("responseTime.markAsResponded")}
                    </a>
                  </Menu.Item>
                )}
                <Menu.Item>
                  <a
                    role="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await dispatch(
                        chatActions.updateChannelStatus({
                          channelId: channel.id,
                          status: archived ? ChatStatus.Active : ChatStatus.Archived,
                        }),
                      );
                      if (archived) {
                        changeTab(ChatStatus.Active);
                      }
                    }}
                  >
                    {archived ? "Aktywuj" : "Zarchiwizuj"}
                  </a>
                </Menu.Item>
              </Menu>
            }
            trigger={["hover"]}
          >
            {updatingChannelId === channel.id || markAsResponded.isLoading ? (
              <Spin size="small" />
            ) : (
              <SettingOutlined />
            )}
          </Dropdown>
        )}
      </List.Item>
    </Tooltip>
  );
};

export default ChatChannelItem;
